import { Button, Col, Form, FormInstance, Row, Space, Switch } from "antd";
import Checkbox from "antd/es/checkbox/Checkbox";
import { NamePath } from "antd/es/form/interface";
import BigNumber from "bignumber.js";
import React, { useState } from "react";
import { LETTER_REGEX_ALLOW_DECIMAL } from "../../constants";
import { DEFAULT_PRODUCT_QUANTITY } from "../../constants/product";
import { ArrowDownIcon, ArrowUpIcon, GradientPlusIcon, TrashIcon } from "../../resources";
import { filterECharacterInputNumber, integerOnlyInput } from "../../utils/functions/integerOnly";
import { calculateCostPrice, calculateSellingPrice } from "../../utils/functions/productMapping";
import { isPositiveNumberOrZero } from "../../utils/functions/validation";
import CustomIcon from "../CustomIcon/CustomIcon";
import CustomInput from "../CustomInput/CustomInput";
import GradientText from "../GradientText/GradientText";
import styles from "./AttributeBox.module.scss";
import { STORE_TYPE } from "../../constants/store";

type Props = {
  onChange?: () => void;
  attributeIndex: number;
  remove: (attributeIndex: number | number[]) => void;
  restAttribute: {
    fieldKey?: number | undefined;
  };
  disabled?: boolean;
  form: FormInstance;
  markup: number;
  isSellingMode: boolean;
  type?: STORE_TYPE;
};

const AttributeBox: React.FC<Props> = ({
  attributeIndex,
  remove,
  restAttribute,
  disabled = false,
  form,
  markup,
  isSellingMode,
  type = STORE_TYPE.FOOD_DELIVERY,
}) => {
  const [open, setOpen] = useState(true);
  const [trigger, setTrigger] = useState(false);
  const isQuantifiable = Form.useWatch(["attributes", attributeIndex, "quantifiable"], form);

  const handleOnChangeInputNumberCostPrice =
    (name: string, setFieldValue: (name: NamePath, value: any) => void, variantIndex: number) => (event: any) => {
      const costPrice = event.target.value && event.target.value.replace(LETTER_REGEX_ALLOW_DECIMAL, "");

      const sellingPrice = Number(costPrice || 0) * (1 + markup / 100);
      const bigInt = new BigNumber(sellingPrice);
      setFieldValue(["attributes", attributeIndex, "variants", variantIndex, name], costPrice);
      setFieldValue(
        ["attributes", attributeIndex, "variants", variantIndex, "price"],
        // Number(sellingPrice) ? bigInt.toFixed(2) : (0).toFixed(2)
        calculateSellingPrice(costPrice, markup)
      );
      form.validateFields([
        ["attributes", attributeIndex, "variants", variantIndex, "costPrice"],
        ["attributes", attributeIndex, "variants", variantIndex, "price"],
      ]);
      // form.validateFields();
    };

  const handleOnChangeInputNumberSellingPrice =
    (name: string, setFieldValue: (name: NamePath, value: any) => void, variantIndex: number) => (event: any) => {
      const sellingPrice = event.target.value && event.target.value.replace(LETTER_REGEX_ALLOW_DECIMAL, "");

      const costPrice = Number(sellingPrice || 0) / (1 + markup / 100);
      const bigInt = new BigNumber(costPrice);
      setFieldValue(["attributes", attributeIndex, "variants", variantIndex, name], sellingPrice);
      setFieldValue(
        ["attributes", attributeIndex, "variants", variantIndex, "costPrice"],
        // Number(costPrice) ? bigInt.toFixed(2, BigNumber.ROUND_UP) : (0).toFixed(2)
        calculateCostPrice(sellingPrice, markup)
      );
      form.validateFields([
        ["attributes", attributeIndex, "variants", variantIndex, "costPrice"],
        ["attributes", attributeIndex, "variants", variantIndex, "price"],
      ]);
      // form.validateFields();
    };

  const handleOnBlurInputNumber =
    (name: string, setFieldValue: (name: NamePath, value: any) => void, variantIndex: number) => async (event: any) => {
      const value = event.target.value.split(".")[1];
      if (!Number(event.target.value)) {
        setFieldValue(["attributes", attributeIndex, "variants", variantIndex, name], Number(0).toFixed(2));
        return;
      }
      if (!value || value.length < 2) {
        setFieldValue(
          ["attributes", attributeIndex, "variants", variantIndex, name],
          Number(event.target.value).toFixed(2)
        );
      }
      if (value && value.length > 2) {
        setFieldValue(
          ["attributes", attributeIndex, "variants", variantIndex, name],
          parseFloat(event.target.value).toFixed(2)
        );
      }
    };

  const handleOnBlurInputAttributeName = (name: string, form: FormInstance, variantIndex: number) => (event: any) => {
    form.setFieldValue(
      ["attributes", attributeIndex, "variants", variantIndex, name],
      event.target.value.replace(/\s+/g, " ").trim()
    );
  };

  const handleOnChangeCheckbox = (event: any, variantIndex: number, name: string) => {
    form.setFieldValue(["attributes", attributeIndex, "variants", variantIndex, name], event.target.checked);
  };

  const handleOnBlurInput = (name: string, form: FormInstance) => (event: any) => {
    form.setFieldValue(["attributes", attributeIndex, name], event.target.value.replace(/\s+/g, " ").trim());
  };

  return (
    <div className={styles.box}>
      <div className={styles.header}>
        <Row gutter={32} style={{ width: "100%" }}>
          <Col lg={24} span={24}>
            <CustomInput
              disabled={disabled}
              label="Attribute name"
              name={[attributeIndex, "attribute_name"]}
              restField={restAttribute}
              placeholder="Attribute name"
              maxLength={50}
              onBlur={handleOnBlurInput("attribute_name", form)}
              rules={[
                {
                  required: true,
                  message: "This field is required",
                  whitespace: true,
                },
              ]}
            />
          </Col>
          <Col xs={24} sm={12}>
            <CustomInput
              type="number"
              name={[attributeIndex, "min"]}
              label="Min required"
              placeholder="0"
              rules={[
                { required: true, message: "This field is required" },
                { validator: isPositiveNumberOrZero("min") },
                {
                  validator: (rule, value) => {
                    if (+value > +form.getFieldValue(["attributes", attributeIndex, "max"])) {
                      return Promise.reject("Min required must be less than or equal to max required");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              onKeyDown={event => {
                integerOnlyInput(event);
              }}
              onChange={event => {
                filterECharacterInputNumber("min", form, event);
                form.validateFields([["attributes", attributeIndex, "min"]]);
                form.validateFields([["attributes", attributeIndex, "max"]]);
              }}
            />
          </Col>{" "}
          <Col xs={24} sm={12}>
            <CustomInput
              type="number"
              name={[attributeIndex, "max"]}
              label="Max required"
              placeholder="0"
              rules={[
                { required: true, message: "This field is required" },
                { validator: isPositiveNumberOrZero("max") },
                {
                  validator: (rule, value) => {
                    if (value <= 0) {
                      return Promise.reject("Max required must be greater than zero");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              onKeyDown={event => {
                integerOnlyInput(event);
              }}
              onChange={event => {
                filterECharacterInputNumber("max", form, event);
                form.validateFields([["attributes", attributeIndex, "min"]]);
                form.validateFields([["attributes", attributeIndex, "max"]]);
              }}
            />
          </Col>
        </Row>
        <div className={styles.action}>
          <div
            className={styles.delete}
            onClick={() => {
              remove(attributeIndex);
            }}
          >
            <CustomIcon icon={TrashIcon} width={20} stroke="currentColor" />
          </div>
          <div className={styles.collapse} onClick={() => setOpen(!open)}>
            <CustomIcon icon={open ? ArrowUpIcon : ArrowDownIcon} width={20} fill="currentColor" />
          </div>
        </div>
      </div>
      <div className={`${styles.content} ${open ? styles.open : ""}`}>
        <div className={styles.contentTitle}>
          Variation list{" "}
          <Space>
            <span>Quantifiable</span>
            <Form.Item name={[attributeIndex, "quantifiable"]} className={styles.toggleButton}>
              <Switch style={{ marginLeft: "15px" }} checked={isQuantifiable} />
            </Form.Item>
          </Space>
        </div>
        <Row className={styles.variationsHeader} gutter={32}>
          <Col span={isQuantifiable ? 5 : 9}>Variant Name *</Col>
          <Col span={5}>Cost Price *</Col>
          <Col span={5}>Selling Price *</Col>
          {isQuantifiable && <Col span={5}>Maximum Quantity *</Col>}
        </Row>
        <Form.Item dependencies={[["attributes", attributeIndex, "variants"]]} noStyle>
          {({ getFieldValue, setFieldValue }) => (
            <Form.List name={[attributeIndex, "variants"]} {...restAttribute}>
              {(variants, { add, remove }) => (
                <>
                  {variants.map(({ key, name: variantIndex, ...restField }) => {
                    if (!getFieldValue(["attributes", attributeIndex, "variants", variantIndex])) return null;
                    return (
                      <Row key={key} className={styles.variations} gutter={[32, 0]}>
                        <Col span={isQuantifiable ? 5 : 9}>
                          <CustomInput
                            disabled={disabled}
                            inputClassName={styles.input}
                            name={[variantIndex, "attribute_value"]}
                            restField={restField}
                            placeholder="Variation name"
                            onBlur={handleOnBlurInputAttributeName("attribute_value", form, variantIndex)}
                            maxLength={50}
                            rules={[{ required: true, message: "This field is required", whitespace: true }]}
                          />
                        </Col>
                        <Col span={5}>
                          <CustomInput
                            disabled={disabled}
                            inputClassName={styles.input}
                            prefix="$"
                            readOnly={isSellingMode ? true : false}
                            name={[variantIndex, "costPrice"]}
                            placeholder="0.00"
                            maxLength={20}
                            rules={[
                              { required: true, message: "This field is required" },
                              { validator: isPositiveNumberOrZero("costPrice") },
                            ]}
                            onChange={handleOnChangeInputNumberCostPrice("costPrice", setFieldValue, variantIndex)}
                            onBlur={handleOnBlurInputNumber("costPrice", setFieldValue, variantIndex)}
                          />
                        </Col>
                        <Col span={5}>
                          <CustomInput
                            disabled={disabled}
                            inputClassName={styles.input}
                            prefix="$"
                            readOnly={isSellingMode ? false : true}
                            name={[variantIndex, "price"]}
                            placeholder="0.00"
                            maxLength={20}
                            rules={[
                              { required: true, message: "This field is required" },
                              { validator: isPositiveNumberOrZero("price") },
                            ]}
                            onChange={handleOnChangeInputNumberSellingPrice("price", setFieldValue, variantIndex)}
                            onBlur={handleOnBlurInputNumber("price", setFieldValue, variantIndex)}
                          />
                        </Col>
                        {isQuantifiable && (
                          <Col span={4}>
                            <CustomInput
                              disabled={disabled}
                              inputClassName={styles.input}
                              name={[variantIndex, "maximumAllowQuantity"]}
                              placeholder="0"
                              rules={[
                                { required: isQuantifiable ? true : false, message: "This field is required" },
                                {
                                  validator: isPositiveNumberOrZero("maximumAllowQuantity"),
                                  message: "Maximum allow quantity must be a positive number",
                                },
                                {
                                  validator: (rule, value) => {
                                    if (type === STORE_TYPE.SURPLUS && Number(value) <= 0) {
                                      return Promise.reject("Maximum quantity required must be greater than zero");
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                              onKeyDown={event => {
                                integerOnlyInput(event);
                              }}
                              onChange={event => {
                                filterECharacterInputNumber("maximumAllowQuantity", form, event);
                              }}
                            />
                          </Col>
                        )}

                        <Col span={3}>
                          <div className={styles.itemCenterWrapper}>
                            <Checkbox
                              disabled={disabled}
                              defaultChecked={getFieldValue([
                                "attributes",
                                attributeIndex,
                                "variants",
                                variantIndex,
                                "isSoldOut",
                              ])}
                              onChange={e => handleOnChangeCheckbox(e, variantIndex, "isSoldOut")}
                            >
                              Out of stock
                            </Checkbox>
                          </div>
                        </Col>
                        <Col span={2}>
                          <div className={styles.itemCenterWrapper}>
                            {variants.length > 1 && (
                              <div
                                className={styles.delete}
                                onClick={() => {
                                  if (getFieldValue(["attributes", attributeIndex, "variants", variantIndex, "id"])) {
                                    const id = getFieldValue([
                                      "attributes",
                                      attributeIndex,
                                      "variants",
                                      variantIndex,
                                      "id",
                                    ]);
                                    const oldValue = getFieldValue(["attributes", attributeIndex, "variants"]);
                                    const arrayVariant = oldValue.filter((item: any) => item.id !== id);
                                    // handleValidateMaxFields(variants.length - 1);
                                    return setFieldValue(["attributes", attributeIndex, "variants"], arrayVariant);
                                  }
                                  remove(variantIndex);
                                  // handleValidateMaxFields(variants.length - 1);
                                }}
                              >
                                <CustomIcon icon={TrashIcon} width={20} stroke="currentColor" />
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    );
                  })}
                  <Button
                    className={styles.add}
                    onClick={() => {
                      add({
                        attribute_value: "",
                        price: "",
                        isSoldOut: false,
                        total_quantity: DEFAULT_PRODUCT_QUANTITY,
                      });
                      // handleValidateMaxFields(variants.length + 1);

                      setTrigger(!trigger);
                    }}
                    type="text"
                    disabled={disabled}
                  >
                    <CustomIcon icon={GradientPlusIcon} width={20} />
                    <GradientText text="Add variation" />
                  </Button>
                </>
              )}
            </Form.List>
          )}
        </Form.Item>
      </div>
    </div>
  );
};

export default AttributeBox;
